import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/api";

const emptyForm = {
  slug: "",
  name: "",
  flavor: "",
  tagline: "",
  price: "",
  protein: "",
  chapter: "",
  accent: "#111111",
  description: "",
  stock: "0",
  weight: "100",
  active: true,
  ingredients: [{ name: "", dose: "" }],
  nutritionJson: "",
};

// Phone photos often exceed the server's per-image limit, so large images are
// scaled down and re-encoded as JPEG before upload. Small files pass through untouched.
const MAX_DIMENSION = 2000;
const COMPRESS_ABOVE_BYTES = 1.5 * 1024 * 1024;

const compressImage = async (file) => {
  if (file.size <= COMPRESS_ABOVE_BYTES || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
};

const ProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  // Gallery order: kept images first, then new uploads. The first image is the main one.
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const newPreviews = useMemo(() => newFiles.map((f) => URL.createObjectURL(f)), [newFiles]);

  useEffect(() => () => newPreviews.forEach((url) => URL.revokeObjectURL(url)), [newPreviews]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get("/admin/products").then(({ data }) => {
      const product = data.products.find((p) => p._id === id);
      if (!product) return;
      setForm({
        slug: product.slug,
        name: product.name,
        flavor: product.flavor || "",
        tagline: product.tagline || "",
        price: String(product.price),
        protein: product.protein || "",
        chapter: product.chapter || "",
        accent: product.accent || "#111111",
        description: product.description || "",
        stock: String(product.stock ?? 0),
        weight: String(product.weight ?? 100),
        active: product.active,
        ingredients: product.ingredients?.length ? product.ingredients : [{ name: "", dose: "" }],
        nutritionJson: product.nutrition ? JSON.stringify(product.nutrition, null, 2) : "",
      });
      setExistingImages(product.images?.length ? product.images : product.image ? [product.image] : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateIngredient = (i, field, value) => {
    const next = [...form.ingredients];
    next[i] = { ...next[i], [field]: value };
    setForm({ ...form, ingredients: next });
  };

  const addIngredient = () => setForm({ ...form, ingredients: [...form.ingredients, { name: "", dose: "" }] });
  const removeIngredient = (i) => setForm({ ...form, ingredients: form.ingredients.filter((_, idx) => idx !== i) });

  const MAX_IMAGES = 10;
  const totalImages = existingImages.length + newFiles.length;

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_IMAGES - totalImages;
    if (picked.length > room) toast.error(`A product can have up to ${MAX_IMAGES} images`);
    setNewFiles((prev) => [...prev, ...picked.slice(0, Math.max(0, room))]);
  };

  const removeExisting = (i) => setExistingImages((prev) => prev.filter((_, idx) => idx !== i));
  const removeNew = (i) => setNewFiles((prev) => prev.filter((_, idx) => idx !== i));
  const makeExistingMain = (i) => setExistingImages((prev) => [prev[i], ...prev.filter((_, idx) => idx !== i)]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let nutrition;
    if (form.nutritionJson.trim()) {
      try {
        nutrition = JSON.parse(form.nutritionJson);
      } catch {
        toast.error("Nutrition JSON is invalid");
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("slug", form.slug.trim());
      fd.append("name", form.name.trim());
      fd.append("flavor", form.flavor);
      fd.append("tagline", form.tagline);
      fd.append("price", form.price);
      fd.append("protein", form.protein);
      fd.append("chapter", form.chapter);
      fd.append("accent", form.accent);
      fd.append("description", form.description);
      fd.append("stock", form.stock);
      fd.append("weight", form.weight);
      fd.append("active", String(form.active));
      fd.append("ingredients", JSON.stringify(form.ingredients.filter((i) => i.name.trim())));
      if (nutrition) fd.append("nutrition", JSON.stringify(nutrition));
      fd.append("existingImages", JSON.stringify(existingImages));
      const prepared = await Promise.all(newFiles.map(compressImage));
      prepared.forEach((file) => fd.append("images", file));

      if (isEdit) {
        await api.put(`/admin/products/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product created");
      }
      navigate("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full border-b border-[#EBE8E3] py-2 outline-none focus:border-[#C07C5F] bg-transparent text-sm";

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif-lux text-3xl mb-8">{isEdit ? "Edit Product" : "Add Product"}</h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-6">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Slug</span>
            <input required disabled={isEdit} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={input} placeholder="collagen-bar" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Name</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Flavor</span>
            <input value={form.flavor} onChange={(e) => setForm({ ...form, flavor: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Tagline</span>
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Price (INR)</span>
            <input required type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Protein</span>
            <input value={form.protein} onChange={(e) => setForm({ ...form, protein: e.target.value })} className={input} placeholder="10g" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Stock</span>
            <input required type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Weight (grams)</span>
            <input required type="number" min="1" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className={input} placeholder="For shipping calculations" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Chapter</span>
            <input value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} className={input} placeholder="01" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Accent Color</span>
            <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-full" />
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Active (visible on site)</span>
          </label>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Description</span>
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
        </label>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Ingredients</span>
            <button type="button" onClick={addIngredient} className="text-[#C07C5F] hover:text-[#111111]"><Plus size={16} /></button>
          </div>
          {form.ingredients.map((ing, i) => (
            <div key={i} className="flex gap-3 mb-2">
              <input placeholder="Name" value={ing.name} onChange={(e) => updateIngredient(i, "name", e.target.value)} className={input} />
              <input placeholder="Dose" value={ing.dose} onChange={(e) => updateIngredient(i, "dose", e.target.value)} className={input} />
              <button type="button" onClick={() => removeIngredient(i)} className="text-[#5C5C5C] hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Nutrition (optional, advanced — JSON)</span>
          <textarea
            rows={6}
            value={form.nutritionJson}
            onChange={(e) => setForm({ ...form, nutritionJson: e.target.value })}
            className={`${input} font-mono text-xs`}
            placeholder='{"serving":"Per Serve — 40g Bar","rows":[{"label":"Energy","value":"185 kcal"}],"actives":[],"ingredients":""}'
          />
        </label>

        <div className="flex flex-col gap-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">
            Images ({totalImages}/{MAX_IMAGES}) — the first one is the main image
          </span>
          {totalImages > 0 && (
            <div className="flex flex-wrap gap-3" data-testid="product-image-gallery">
              {existingImages.map((url, i) => (
                <div key={url} className={`relative w-32 border ${i === 0 ? "border-[#C07C5F]" : "border-[#EBE8E3]"}`}>
                  <img src={resolveImage(url)} alt="" className="w-full aspect-square object-cover" />
                  <div className="flex items-center justify-between px-1.5 py-1 text-[9px] tracking-[0.15em] uppercase">
                    {i === 0 ? (
                      <span className="text-[#C07C5F]">Main</span>
                    ) : (
                      <button type="button" onClick={() => makeExistingMain(i)} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#C07C5F]">
                        <Star size={10} /> Make main
                      </button>
                    )}
                    <button type="button" onClick={() => removeExisting(i)} aria-label="Remove image" className="text-[#5C5C5C] hover:text-red-600">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))}
              {newPreviews.map((url, i) => (
                <div key={url} className={`relative w-32 border border-dashed ${existingImages.length === 0 && i === 0 ? "border-[#C07C5F]" : "border-[#EBE8E3]"}`}>
                  <img src={url} alt="" className="w-full aspect-square object-cover" />
                  <div className="flex items-center justify-between px-1.5 py-1 text-[9px] tracking-[0.15em] uppercase">
                    <span className="text-[#5C5C5C]">{existingImages.length === 0 && i === 0 ? "Main · new" : "New"}</span>
                    <button type="button" onClick={() => removeNew(i)} aria-label="Remove image" className="text-[#5C5C5C] hover:text-red-600">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {totalImages < MAX_IMAGES && (
            <input type="file" accept="image/*" multiple onChange={addFiles} className="text-sm" data-testid="product-images-input" />
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-[#111111] text-white rounded-full py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : isEdit ? "Update Product" : "Create Product"}
        </button>
      </form>
    </div>
  );
};

export default ProductForm;
